// Components
import {
  Alert,
  Stack,
  Select,
  MultiSelect,
  Textarea,
  Skeleton,
  Text,
  List,
} from "@mantine/core";
import {
  IconAlertTriangle,
  IconBrain,
  IconBottle,
  IconAlertTriangle as IconCrisis,
  IconUsers,
  IconInfoCircle,
} from "@tabler/icons-react";
import { FormSection } from "@benchmark/ui/components/atomic";
import { FormField } from "@benchmark/ui/components/atomic";
import { FormGroup } from "@benchmark/ui/components/atomic";
import { FormStep, SteppedForm } from "@benchmark/ui/components/atomic";
import type { FormStep as FormStepType } from "@benchmark/ui/components/atomic";
import { CardSelect } from "@benchmark/ui/components/atomic";
import { ExtractedDataHint } from "@/components/domain/healthcare-service/ExtractedDataHint";
import { ContactDetailsSection } from "@/components/shared/forms/ContactDetailsSection";
import { CoverageAreasSection } from "@/components/shared/forms/CoverageAreasSection";
import { DimensionValueInput } from "@/components/shared/forms/DimensionValueInput";
import { LocationSection } from "@/components/shared/forms/LocationSection";
import { getLocationSectionErrors } from "@/components/shared/forms/LocationSection.validation";
import { OrganizationSection } from "@/components/shared/forms/OrganizationSection";
import { getOrganizationSectionErrors } from "@/components/shared/forms/OrganizationSection.validation";
import { formatAddress } from "@benchmark/core/utils";

// Constants
import {
  LOCATION_OPTIONS_COUNT,
  ORGANIZATION_OPTIONS_COUNT,
} from "@benchmark/core/constants";
import {
  // Core service constants
  SERVICE_PROVISION_OPTIONS,
  SERVICE_TYPE_OPTIONS,
  // Specialty constants
  SERVICE_SPECIALTY_TREATMENT_MODALITY_OPTIONS,
  SERVICE_SPECIALTY_TREATMENT_CONDITION_OPTIONS,
  // Characteristic constants
  SERVICE_CHARACTERISTIC_VISIT_MODE_OPTIONS,
  SERVICE_CHARACTERISTIC_ACCESS_OPTIONS,
  SERVICE_CHARACTERISTIC_AVAILABILITY_OPTIONS,
  SERVICE_CHARACTERISTIC_SCHEDULING_OPTIONS,
  SERVICE_CHARACTERISTIC_WAIT_TIME_OPTIONS,
  // Eligibility constants
  SERVICE_ELIGIBILITY_DIMENSION_OPTIONS,
  SERVICE_ELIGIBILITY_AGE_GROUP_OPTIONS,
  SERVICE_ELIGIBILITY_GENDER_OPTIONS,
  HEALTHCARE_SERVICE_CODESYSTEMS,
  // Helper functions
  getServiceTypesByCategory,
} from "@benchmark/core/constants";

// Hooks
import { useForm } from "@mantine/form";
import { useMedplum, useResource } from "@medplum/react";
import { useCallback, useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router";
import { useFormSubmission } from "@benchmark/ui/hooks";
import { useFormProtection } from "@benchmark/ui/hooks";
import { useProviderOrganizationSearch } from "@/hooks/shared/useFHIROptions";
import { useOrganizationResource } from "@/hooks/shared/useFHIRResource";
import { useSiblingHealthcareServices } from "@/hooks/domain/useSiblingHealthcareServices";

// Types
import type {
  HealthcareService,
  Organization,
  Location,
} from "@medplum/fhirtypes";
import type { HealthcareServiceFormData } from "@benchmark/core/types";
import type { EnhancedSelectOption } from "@/types/ui/component-props";
import type { ComboboxProps } from "@mantine/core";

// Utils
import { healthcareServiceToForm } from "@benchmark/core/adapters";
// import { healthcareServiceFromForm } from "@benchmark/core/adapters";
import {
  showErrorNotification,
  showSuccessNotification,
} from "@benchmark/ui/utils";
import { createHealthcareServiceFacade } from "@/facades/healthcare-service.facade";

// Validators
// COMMENTED OUT: Conformance checking is disabled
// import {
//   checkHealthcareServiceConformance,
//   mapConformanceIssuesToFields,
// } from "@benchmark/core/validators";

// Interfaces

/**
 * Raw crawled data for fields that couldn't be auto-matched to form options.
 * @agentic connections
 */
type ImportMeta = {
  extractedServices: string[] | null;
  extractedLocations: string[] | null;
  extractedInsurance: string[] | null;
};

/**
 * Props for the HealthcareServiceForm component.
 * @agentic connections
 */
type HealthcareServiceFormProps = {
  healthcareService?: HealthcareService;
  /** Initial form values for pre-filling (e.g., from website extraction) */
  initialValues?: Partial<Record<string, unknown>>;
  /** Raw crawled data for fields that couldn't be auto-matched */
  importMeta?: ImportMeta;
  onSuccess?: (healthcareServiceId: string) => void;
  onCancel?: () => void;
};

/**
 * Forces dropdown to render below the input, preventing upward flipping inside modals.
 * @agentic connections
 */
const DROPDOWN_BELOW: ComboboxProps = {
  position: "bottom",
  middlewares: { flip: false, shift: false },
};

/**
 * Stepped form for creating and editing FHIR HealthcareService resources.
 * @agentic connections
 */
export const HealthcareServiceForm = ({
  healthcareService: propHealthcareService,
  initialValues: propInitialValues,
  importMeta: propImportMeta,
  onSuccess,
  onCancel,
}: HealthcareServiceFormProps = {}) => {
  const navigate = useNavigate();
  const medplum = useMedplum();
  const healthcareServiceFacade = createHealthcareServiceFacade(medplum);
  const { id } = useParams();

  // Use prop healthcareService if provided, otherwise fetch from URL
  const fetchedHealthcareService = useResource<HealthcareService>(
    id ? { reference: `HealthcareService/${id}` } : undefined,
  );
  const existingHealthcareService =
    propHealthcareService || fetchedHealthcareService;
  const isEditMode = !!existingHealthcareService?.id;

  // Simplified state management
  const [insuranceOrganizations, setInsuranceOrganizations] = useState<
    Organization[]
  >([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState({
    insurance: true,
    locations: true,
  });

  // Async organization search state
  const [organizationSearchQuery, setOrganizationSearchQuery] = useState("");

  // Fetch organization search results
  const {
    data: organizationSearchResults,
    isLoading: isSearchingOrganizations,
  } = useProviderOrganizationSearch(organizationSearchQuery);

  // Replace single duplicateServices state with array of duplicate type codes
  const [duplicateServiceTypes, setDuplicateServiceTypes] = useState<string[]>(
    [],
  );

  // State for duplicate organization warning
  const [duplicateOrganization, setDuplicateOrganization] = useState<{
    id: string;
    name: string;
  } | null>(null);

  // Conformance checking state
  // COMMENTED OUT: Conformance checking is disabled
  // const [conformanceIssues, setConformanceIssues] = useState<
  //   Record<string, string[]>
  // >({});
  // const [isConformant, setIsConformant] = useState(true);
  const [conformanceIssues] = useState<Record<string, string[]>>({});
  const [isConformant] = useState(true);

  const importMeta = propImportMeta ?? null;

  const { isSubmitting, createSubmissionHandler } = useFormSubmission();

  const form = useForm<HealthcareServiceFormData>({
    initialValues: {
      name: "",
      description: "",
      category: "",
      type: "", // Keep for edit mode
      "type-display": "",
      types: [], // NEW: for create mode
      "types-displays": [], // NEW: display names
      specialty: [],
      careModality: [], // Changed to array for multi-select
      partnershipType: "",
      providedBy: "",
      providerOrganization: {
        isNew: false,
      },
      location: [], // Keep for backward compatibility
      locations: [], // NEW: Structured location data
      coverageAreas: [], // NEW: Geographic coverage areas (state-level)
      eligibility: [],
      eligibilityCriteria: "",
      program: [],
      insurances: [], // Simplified insurance field
      active: "true",
      comment: "",
      extraDetails: "",
      serviceProvisionCode: [],
      characteristic: [],
      telecom: [],
      identifier: [],
    },
    validate: {
      // Only Organization, Category, and Type are required
      // Organization can be either providedBy (existing) or providerOrganization (new)
      providedBy: (
        value: string | undefined,
        values: HealthcareServiceFormData,
      ) => {
        // If creating a new organization, skip this validation (handled by step validation)
        if (values.providerOrganization?.isNew) {
          return null;
        }
        // If using new format with existing organization, skip this validation
        if (values.providerOrganization?.existingId) {
          return null;
        }
        // Otherwise, require providedBy (legacy format)
        return !value ? "Provider organization is required" : null;
      },
      category: (value: string | undefined) =>
        !value ? "Category is required" : null,
      type: (value: string | undefined) => {
        if (!isEditMode) {
          return null;
        }
        return !value ? "Service type is required" : null;
      },
      types: (value: string[] | undefined) => {
        if (isEditMode) {
          return null;
        }
        return !value || value.length === 0
          ? "At least one service type is required"
          : null;
      },
      // Removed validation for: name (auto-generated), careModality, location, insurances
    },
  });

  const { markAsClean, ConfirmationModal } = useFormProtection(form, {
    enabled: true,
    blockNavigation: true,
    confirmationMessage:
      "You have unsaved service changes. Are you sure you want to close?",
  });

  // Consolidated data fetching with error handling
  // Organizations now use async search, so only fetch insurance and locations
  const fetchData = useCallback(async () => {
    try {
      const [insuranceResult, locationsResult] = await Promise.all([
        medplum.searchResources("Organization", {
          _count: ORGANIZATION_OPTIONS_COUNT.toString(),
          _sort: "name",
          active: "true",
          type: "ins",
        }),
        medplum.searchResources("Location", {
          _count: LOCATION_OPTIONS_COUNT.toString(),
          _sort: "name",
          status: "active",
        }),
      ]);

      setInsuranceOrganizations(insuranceResult);
      setLocations(locationsResult);
    } catch (error) {
      console.error("Error fetching data:", error);
      showErrorNotification(
        "Error loading form data. Please refresh the page.",
      );
    } finally {
      setLoading({ insurance: false, locations: false });
    }
  }, [medplum]);

  useEffect(() => {
    queueMicrotask(() => {
      void fetchData();
    });
  }, [fetchData]);

  // Fetch selected organization for display when not searching
  // This must be after form declaration since it uses form.values
  const selectedOrganizationId =
    form.values.providerOrganization?.existingId || form.values.providedBy;

  // Fetch sibling services for batch edit mode
  const { siblings: siblingServices, organizationName: siblingOrgName } =
    useSiblingHealthcareServices(
      isEditMode ? selectedOrganizationId : undefined,
      existingHealthcareService?.id,
    );

  const { data: selectedOrganization } = useOrganizationResource(
    selectedOrganizationId && !organizationSearchQuery.trim()
      ? selectedOrganizationId
      : undefined,
  );

  const existingHealthcareServiceId = existingHealthcareService?.id;
  const [prefilledFieldLocks, setPrefilledFieldLocks] = useState({
    providedBy: false,
    category: false,
    type: false,
  });
  const shouldLockProviderOrganization =
    !!existingHealthcareServiceId && prefilledFieldLocks.providedBy;
  const shouldLockServiceCategory =
    !!existingHealthcareServiceId && prefilledFieldLocks.category;
  const shouldLockServiceType =
    !!existingHealthcareServiceId && prefilledFieldLocks.type;

  // Check for duplicate services when org or types change
  useEffect(() => {
    // In edit mode, use single type; in create mode, use types array
    const selectedTypes = existingHealthcareService
      ? form.values.type
        ? [form.values.type]
        : []
      : form.values.types || [];

    if (!selectedOrganizationId || selectedTypes.length === 0) {
      queueMicrotask(() => {
        setDuplicateServiceTypes([]);
      });
      return;
    }

    let isActive = true;

    const checkDuplicates = async () => {
      try {
        // Fetch existing services for this organization
        const existingServices = await medplum.searchResources(
          "HealthcareService",
          {
            organization: `Organization/${selectedOrganizationId}`,
            _count: "100",
          },
        );

        // Extract existing type codes
        const existingTypeCodes = new Set(
          existingServices
            .filter((svc) => svc.id !== existingHealthcareServiceId)
            .flatMap(
              (svc) =>
                svc.type?.flatMap(
                  (t) =>
                    t.coding
                      ?.filter(
                        (c) =>
                          c.system ===
                          HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_TYPE,
                      )
                      .map((c) => c.code) || [],
                ) || [],
            ),
        );

        // Find duplicates among selected types
        const duplicates = selectedTypes.filter((type) =>
          existingTypeCodes.has(type),
        );

        if (isActive) {
          setDuplicateServiceTypes(duplicates);
        }
      } catch (error) {
        console.error("Error checking for duplicate services:", error);
        if (isActive) {
          setDuplicateServiceTypes([]);
        }
      }
    };

    checkDuplicates();

    return () => {
      isActive = false;
    };
  }, [
    selectedOrganizationId,
    form.values.types,
    form.values.type,
    existingHealthcareService,
    existingHealthcareServiceId,
    medplum,
  ]);

  // Check for duplicate organizations when creating new org with name and type
  useEffect(() => {
    const orgData = form.values.providerOrganization;

    // Only check when creating a new organization with both name and type
    if (!orgData?.isNew || !orgData.name?.trim() || !orgData.type) {
      queueMicrotask(() => {
        setDuplicateOrganization(null);
      });
      return;
    }

    let isActive = true;

    // Normalize name for comparison: lowercase, collapse all whitespace to single space, trim
    const normalizeName = (name: string) =>
      name.toLowerCase().replace(/\s+/g, " ").trim();

    const checkDuplicateOrganization = async () => {
      try {
        // Search for organizations with matching type
        // Note: Medplum/FHIR name search is case-insensitive by default
        const existingOrgs = await medplum.searchResources("Organization", {
          name: orgData.name!.trim(),
          type: orgData.type,
          _count: "10", // Get a few results to check for normalized matches
        });

        if (!isActive) return;

        // Check for normalized name match (case-insensitive, whitespace-normalized)
        const normalizedInput = normalizeName(orgData.name!);
        const matchingOrg = existingOrgs.find(
          (org) => org.name && normalizeName(org.name) === normalizedInput,
        );

        if (matchingOrg) {
          setDuplicateOrganization({
            id: matchingOrg.id!,
            name: matchingOrg.name!,
          });
        } else {
          setDuplicateOrganization(null);
        }
      } catch (error) {
        console.error("Error checking for duplicate organization:", error);
        if (isActive) {
          setDuplicateOrganization(null);
        }
      }
    };

    // Debounce the check to avoid excessive API calls while typing
    const timeoutId = setTimeout(checkDuplicateOrganization, 300);

    return () => {
      isActive = false;
      clearTimeout(timeoutId);
    };
  }, [
    form.values.providerOrganization?.isNew,
    form.values.providerOrganization?.name,
    form.values.providerOrganization?.type,
    medplum,
  ]);

  const hasLoadedHealthcareService = useRef(false);
  const loadedHealthcareServiceId = useRef<string | undefined>(undefined);

  // Populate form with existing data
  useEffect(() => {
    if (!existingHealthcareService?.id) {
      hasLoadedHealthcareService.current = false;
      loadedHealthcareServiceId.current = undefined;
      queueMicrotask(() => {
        setPrefilledFieldLocks({
          providedBy: false,
          category: false,
          type: false,
        });
      });
      return;
    }

    if (
      hasLoadedHealthcareService.current &&
      loadedHealthcareServiceId.current === existingHealthcareService.id
    ) {
      return;
    }

    const formData = healthcareServiceToForm(existingHealthcareService);

    // Convert legacy location array to new format if needed
    if (
      formData.location &&
      formData.location.length > 0 &&
      (!formData.locations || formData.locations.length === 0)
    ) {
      formData.locations = formData.location.map((locId, index) => ({
        isNew: false,
        existingId: locId,
        existingDisplay:
          formData["location-displays"]?.[index] || `Location/${locId}`,
      }));
    }

    form.setValues(formData);

    // Explicitly set locations array after setValues
    // Mantine form can sometimes have issues with nested arrays in setValues
    if (formData.locations && formData.locations.length > 0) {
      form.setFieldValue("locations", formData.locations);
    }
    form.resetDirty();
    // Lock fields that were pre-filled from the existing resource.
    // Services without an organization (providedBy is empty) should have the
    // organization toggle unlocked so users can add one.
    queueMicrotask(() => {
      setPrefilledFieldLocks({
        providedBy: !!formData.providedBy,
        category: !!formData.category,
        type: !!formData.type,
      });
    });
    hasLoadedHealthcareService.current = true;
    loadedHealthcareServiceId.current = existingHealthcareService.id;
  }, [existingHealthcareService?.id]);

  // Apply initial values from props (e.g., from website extraction via page-level import)
  const hasAppliedInitialValues = useRef(false);
  useEffect(() => {
    if (propInitialValues && !hasAppliedInitialValues.current && !isEditMode) {
      for (const [key, value] of Object.entries(propInitialValues)) {
        if (value !== undefined) {
          form.setFieldValue(key, value);
        }
      }
      hasAppliedInitialValues.current = true;
    }
  }, [propInitialValues, isEditMode]);

  // Check conformance when form values change
  // COMMENTED OUT: Conformance checking causes errors when form data is incomplete
  // TODO(#988): Re-enable conformance checking once form validation is more robust
  // useEffect(() => {
  //   if (existingHealthcareService) {
  //     try {
  //       // Convert current form values to HealthcareService
  //       const currentService = healthcareServiceFromForm(
  //         form.values,
  //         existingHealthcareService,
  //       );

  //       // Check conformance
  //       const conformance = checkHealthcareServiceConformance(currentService);
  //       const fieldIssues = mapConformanceIssuesToFields(
  //         conformance,
  //         currentService,
  //       );
  //       setConformanceIssues(fieldIssues);
  //       setIsConformant(conformance.isConformant);
  //     } catch (error) {
  //       // Silently fail conformance checking if there's an error
  //       // (e.g., invalid form data that can't be converted)
  //       console.error("Error checking conformance:", error);
  //     }
  //   }
  // }, [form.values, existingHealthcareService]);

  // Filter type options based on selected category
  const filteredTypeOptions = useMemo(
    () => getServiceTypesByCategory(form.values.category),
    [form.values.category],
  );

  const handleTypeChange = (value: string | null) => {
    form.setFieldValue("type", value || "");
    if (value) {
      const typeOption = filteredTypeOptions.find((opt) => opt.value === value);
      form.setFieldValue("type-display", typeOption?.label || "");
    } else {
      form.setFieldValue("type-display", "");
    }
  };

  // NEW: Handle multi-select type changes
  const handleTypesChange = (values: string[]) => {
    form.setFieldValue("types", values);

    // Build display names array
    const displays = values.map((value) => {
      const typeOption = filteredTypeOptions.find((opt) => opt.value === value);
      return typeOption?.label || "";
    });
    form.setFieldValue("types-displays", displays);
  };

  // Handle organization change - display name is now handled by OrganizationSection
  const handleOrganizationChange = (value: string | null) => {
    form.setFieldValue("providedBy", value || "");
    // Display name is set by OrganizationSection component
  };

  // Computed values for specialty MultiSelects
  const selectedConditions = useMemo(
    () =>
      (form.values.specialty || [])
        .filter((s) => s.dimension === "treatmentCondition")
        .map((s) => s.value),
    [form.values.specialty],
  );

  const selectedModalities = useMemo(
    () =>
      (form.values.specialty || [])
        .filter((s) => s.dimension === "treatmentModality")
        .map((s) => s.value),
    [form.values.specialty],
  );

  // Handlers for specialty MultiSelects
  const handleConditionsChange = useCallback(
    (values: string[]) => {
      const modalities = (form.values.specialty || []).filter(
        (s) => s.dimension === "treatmentModality",
      );
      const conditions = values.map((value) => {
        const option = SERVICE_SPECIALTY_TREATMENT_CONDITION_OPTIONS.find(
          (o) => o.value === value,
        );
        return {
          dimension: "treatmentCondition",
          value,
          valueDisplay: option?.label,
        };
      });
      form.setFieldValue("specialty", [...modalities, ...conditions]);
    },
    [form.values.specialty],
  );

  const handleModalitiesChange = useCallback(
    (values: string[]) => {
      const conditions = (form.values.specialty || []).filter(
        (s) => s.dimension === "treatmentCondition",
      );
      const modalities = values.map((value) => {
        const option = SERVICE_SPECIALTY_TREATMENT_MODALITY_OPTIONS.find(
          (o) => o.value === value,
        );
        return {
          dimension: "treatmentModality",
          value,
          valueDisplay: option?.label,
        };
      });
      form.setFieldValue("specialty", [...conditions, ...modalities]);
    },
    [form.values.specialty],
  );

  // Computed values for characteristic fields
  const selectedAccess = useMemo(
    () =>
      (form.values.characteristic || []).find((c) => c.dimension === "access")
        ?.value || null,
    [form.values.characteristic],
  );

  const selectedAvailability = useMemo(
    () =>
      (form.values.characteristic || [])
        .filter((c) => c.dimension === "availability")
        .map((c) => c.value),
    [form.values.characteristic],
  );

  const selectedScheduling = useMemo(
    () =>
      (form.values.characteristic || [])
        .filter((c) => c.dimension === "scheduling")
        .map((c) => c.value),
    [form.values.characteristic],
  );

  const selectedWaitTime = useMemo(
    () =>
      (form.values.characteristic || []).find((c) => c.dimension === "waitTime")
        ?.value || null,
    [form.values.characteristic],
  );

  // Handlers for characteristic fields
  const updateCharacteristic = useCallback(
    (
      dimension: string,
      values: string | string[] | null,
      options: ReadonlyArray<{
        readonly value: string;
        readonly label: string;
      }>,
    ) => {
      const otherCharacteristics = (form.values.characteristic || []).filter(
        (c) => c.dimension !== dimension,
      );

      if (values === null || (Array.isArray(values) && values.length === 0)) {
        form.setFieldValue("characteristic", otherCharacteristics);
        return;
      }

      const valueArray = Array.isArray(values) ? values : [values];
      const newCharacteristics = valueArray.map((value) => {
        const option = options.find((o) => o.value === value);
        return {
          dimension,
          value,
          valueDisplay: option?.label,
        };
      });

      form.setFieldValue("characteristic", [
        ...otherCharacteristics,
        ...newCharacteristics,
      ]);
    },
    [form.values.characteristic],
  );

  const handleAccessChange = useCallback(
    (value: string | null) => {
      updateCharacteristic(
        "access",
        value,
        SERVICE_CHARACTERISTIC_ACCESS_OPTIONS,
      );
    },
    [updateCharacteristic],
  );

  const handleAvailabilityChange = useCallback(
    (values: string[]) => {
      updateCharacteristic(
        "availability",
        values,
        SERVICE_CHARACTERISTIC_AVAILABILITY_OPTIONS,
      );
    },
    [updateCharacteristic],
  );

  const handleSchedulingChange = useCallback(
    (values: string[]) => {
      updateCharacteristic(
        "scheduling",
        values,
        SERVICE_CHARACTERISTIC_SCHEDULING_OPTIONS,
      );
    },
    [updateCharacteristic],
  );

  const handleWaitTimeChange = useCallback(
    (value: string | null) => {
      updateCharacteristic(
        "waitTime",
        value,
        SERVICE_CHARACTERISTIC_WAIT_TIME_OPTIONS,
      );
    },
    [updateCharacteristic],
  );

  // Create enhanced category options for CardSelect
  const CATEGORY_CARD_OPTIONS: EnhancedSelectOption[] = useMemo(
    () => [
      {
        value: "mental-health",
        label: "Mental Health",
        icon: <IconBrain size={24} />,
      },
      {
        value: "substance-use",
        label: "Substance Use",
        icon: <IconBottle size={24} />,
      },
      {
        value: "crisis-services",
        label: "Crisis Services",
        icon: <IconCrisis size={24} />,
      },
      {
        value: "social-services",
        label: "Social Services",
        icon: <IconUsers size={24} />,
      },
    ],
    [],
  );

  // Define form steps configuration
  const formSteps: FormStepType<HealthcareServiceFormData>[] = useMemo(
    () => [
      {
        id: "service-identity",
        title: "Overview",
        subtitle: "Enter the service's organization, category, and type",
        requiredFields: ["category"],
        validate: (values: HealthcareServiceFormData) => {
          const errors: Record<string, string> = {};

          // Check provider organization (new format takes precedence)
          if (values.providerOrganization?.isNew) {
            const orgErrors = getOrganizationSectionErrors(
              values.providerOrganization,
              "providerOrganization",
            );
            Object.assign(errors, orgErrors);
          } else if (
            !values.providerOrganization?.existingId &&
            !values.providedBy
          ) {
            errors.providedBy = "Please select a provider organization";
          }

          if (!values.category) {
            errors.category = "Please select a service category";
          }
          if (isEditMode) {
            if (!values.type) {
              errors.type = "Please select a service type";
            }
          } else if (!values.types || values.types.length === 0) {
            errors.types = "Please select at least one service type";
          }

          return Object.keys(errors).length > 0 ? errors : null;
        },
      },
      {
        id: "service-details",
        title: "Details",
        subtitle: "Specify specialties and eligibility criteria",
        optional: true,
      },
      {
        id: "service-access",
        title: "Access",
        subtitle: "Add information on how members access the service",
        optional: true,
      },
      {
        id: "service-locations",
        title: "Locations",
        subtitle:
          "Add service delivery locations and the geographic areas this service covers",
        optional: true,
        // Step is always visible — coverage areas apply to virtual-only
        // services too, even when there are no physical delivery
        // locations.
        validate: (values: HealthcareServiceFormData) => {
          const locationErrors = getLocationSectionErrors(
            values.locations,
            "locations",
          );

          return Object.keys(locationErrors).length > 0 ? locationErrors : null;
        },
      },
      {
        id: "service-payment",
        title: "Payment",
        subtitle: "Enter payment and insurance information",
        optional: true,
      },
      {
        id: "service-misc",
        title: "Misc.",
        subtitle: "Add contact information and additional details",
        optional: true,
      },
    ],
    [isEditMode],
  );

  const handleSubmit = createSubmissionHandler(
    async (values: HealthcareServiceFormData) => {
      try {
        // Handle provider organization - new format takes precedence
        let providedByValue: string | undefined;
        let providedByDisplay: string | undefined;

        if (values.providerOrganization?.isNew) {
          // New organization - will be created in bundle
          providedByValue = values.providerOrganization.name || "";
          providedByDisplay = values.providerOrganization.name || "";
        } else if (values.providerOrganization?.existingId) {
          // Existing organization from new format
          providedByValue = values.providerOrganization.existingId;
          providedByDisplay = values.providerOrganization.existingDisplay;
        } else if (values.providedBy) {
          // Legacy format
          providedByValue = values.providedBy;
          providedByDisplay = values["providedBy-display"] || values.providedBy;
        }

        // Convert locations to legacy format for backward compatibility
        // (The builder will use the new format if available)
        const locationIds: string[] = [];
        const locationDisplays: string[] = [];

        (values.locations || []).forEach((loc) => {
          if (!loc.isNew && loc.existingId) {
            locationIds.push(loc.existingId);
            locationDisplays.push(
              loc.existingDisplay || `Location/${loc.existingId}`,
            );
          }
          // New locations will be handled by the builder
        });

        // Extract insurance display names for the simplified insurances field
        const insuranceDisplays = (values.insurances || []).map(
          (insuranceId) => {
            const insuranceOption = insuranceOrganizationOptions.find(
              (opt) => opt.value === insuranceId,
            );
            return insuranceOption?.label || `Insurance/${insuranceId}`;
          },
        );

        // Validate that we have display names for required references
        // Skip this check for new organizations (display will be set from name)
        if (
          providedByValue &&
          !providedByDisplay &&
          !values.providerOrganization?.isNew
        ) {
          showErrorNotification(
            "Selected organization does not have a display name. Please contact engineering.",
          );
          return;
        }

        // Validate new organization has required fields
        if (values.providerOrganization?.isNew) {
          if (!values.providerOrganization.name) {
            showErrorNotification("Organization name is required.");
            return;
          }
          if (!values.providerOrganization.type) {
            showErrorNotification("Organization type is required.");
            return;
          }
        }

        if (
          locationIds.length > 0 &&
          locationDisplays.some((display) => display.includes("Location/"))
        ) {
          showErrorNotification(
            "One or more selected locations do not have display names. Please contact engineering.",
          );
          return;
        }

        // Program validation is now handled by DimensionValueInput component

        // Filter out duplicate types for new services
        if (!existingHealthcareService) {
          const selectedTypes = values.types || [];
          const typesToCreate = selectedTypes.filter(
            (type) => !duplicateServiceTypes.includes(type),
          );

          if (typesToCreate.length === 0) {
            showErrorNotification(
              "All selected service types already exist for this organization.",
            );
            return;
          }

          // Build enhanced values with filtered types
          const enhancedValues: HealthcareServiceFormData = {
            ...values,
            types: typesToCreate,
            "types-displays": typesToCreate.map((type) => {
              const idx = selectedTypes.indexOf(type);
              return values["types-displays"]?.[idx] || type;
            }),
            active: "true",
            providedBy: providedByValue || "",
            "providedBy-display": providedByDisplay,
            location: locationIds, // For backward compatibility
            "location-displays": locationDisplays,
            // locations array is already in values
            "insurances-displays": insuranceDisplays,
          };

          const result = await healthcareServiceFacade.upsert({
            formData: enhancedValues,
            existingHealthcareService,
          });

          // Show appropriate success message
          if (result.isUpdate) {
            showSuccessNotification("Healthcare service updated successfully");
          } else if (result.createdCount === 1) {
            showSuccessNotification("Healthcare service created successfully");
          } else {
            showSuccessNotification(
              `${result.createdCount} healthcare services created successfully`,
            );
          }

          markAsClean();

          if (onSuccess) {
            onSuccess(result.healthcareServices[0].id!);
          } else if (result.healthcareServices.length === 1) {
            navigate(`/HealthcareService/${result.healthcareServices[0].id}`);
          } else {
            const orgId =
              values.providerOrganization?.existingId || values.providedBy;
            navigate(`/HealthcareService?organization=${orgId}`);
          }
          return;
        }

        // Edit mode: use batch update for all sibling services
        const getTypeDisplay = (code: string): string => {
          const option = SERVICE_TYPE_OPTIONS.find((opt) => opt.value === code);
          return option?.label || code;
        };
        const typeDisplay =
          values["type-display"] ||
          (values.type ? getTypeDisplay(values.type) : "");
        const generatedName =
          typeDisplay && providedByDisplay
            ? `${typeDisplay} by ${providedByDisplay}`
            : values.name;

        // Enhance form data with display names
        const enhancedValues: HealthcareServiceFormData = {
          ...values,
          name: generatedName || values.name,
          active: "true",
          providedBy: providedByValue || "",
          "providedBy-display": providedByDisplay,
          location: locationIds, // For backward compatibility
          "location-displays": locationDisplays,
          // locations array is already in values
          "insurances-displays": insuranceDisplays,
        };

        // Use batch update if there are sibling services
        if (siblingServices.length > 0) {
          const result = await healthcareServiceFacade.batchUpdate({
            formData: enhancedValues,
            primaryService: existingHealthcareService,
            siblingServices,
          });

          showSuccessNotification(
            `${result.updatedCount} healthcare services updated successfully`,
          );

          markAsClean();

          if (onSuccess) {
            onSuccess(existingHealthcareService.id!);
          } else {
            navigate(`/HealthcareService/${existingHealthcareService.id}`);
          }
          return;
        }

        // No siblings: use standard single-service update
        const result = await healthcareServiceFacade.upsert({
          formData: enhancedValues,
          existingHealthcareService,
        });

        showSuccessNotification("Healthcare service updated successfully");

        markAsClean();

        if (onSuccess) {
          onSuccess(result.healthcareServices[0].id!);
        } else {
          navigate(`/HealthcareService/${result.healthcareServices[0].id}`);
        }
      } catch (error) {
        console.error("Error saving healthcare service:", error);
        showErrorNotification(
          `Failed to ${existingHealthcareService ? "update" : "create"} healthcare service. Please try again.`,
        );
      }
    },
  );

  // Dynamic submit button text based on selection
  const submitButtonText = useMemo(() => {
    if (existingHealthcareService) {
      return "Update Service";
    }

    const typesToCreate = (form.values.types || []).filter(
      (type) => !duplicateServiceTypes.includes(type),
    ).length;

    if (typesToCreate === 0) {
      return "No New Services to Create";
    }
    if (typesToCreate === 1) {
      return "Create Service";
    }
    return `Create ${typesToCreate} Services`;
  }, [existingHealthcareService, form.values.types, duplicateServiceTypes]);

  // Organization options are now handled by async search in OrganizationSection

  // Location options with full address display
  // Include fetched locations + any pre-selected locations from the form
  const locationOptions = useMemo(() => {
    const fetchedOptions = locations.map((loc) => {
      const addressStr = loc.address
        ? formatAddress(loc.address, "oneLine")
        : null;

      const label = addressStr
        ? `${loc.name || "Location"} - ${addressStr}`
        : loc.name || `Location ${loc.id}`;

      return {
        value: loc.id as string,
        label,
      };
    });

    // Add any pre-selected locations that aren't in the fetched list
    // This ensures existing locations always show in the dropdown when editing
    const fetchedIds = new Set(fetchedOptions.map((opt) => opt.value));
    const preSelectedOptions = (form.values.locations || [])
      .filter(
        (loc) =>
          !loc.isNew && loc.existingId && !fetchedIds.has(loc.existingId),
      )
      .map((loc) => ({
        value: loc.existingId as string,
        label: loc.existingDisplay || `Location ${loc.existingId}`,
      }));

    return [...preSelectedOptions, ...fetchedOptions];
  }, [locations, form.values.locations]);

  if (id && !existingHealthcareService) {
    return (
      <Stack gap="md">
        <Skeleton height={32} width="40%" />
        <Skeleton height={14} width="30%" />
        <Skeleton height={36} />
        <Skeleton height={14} width="25%" />
        <Skeleton height={36} />
        <Skeleton height={44} width="200px" />
      </Stack>
    );
  }

  const insuranceOrganizationOptions = insuranceOrganizations.map((org) => ({
    value: org.id as string,
    label: org.name || `Insurance ${org.id}`,
  }));

  return (
    <>
      <SteppedForm
        form={form}
        steps={formSteps}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        submitText={submitButtonText}
        onCancel={onCancel}
        allowFreeNavigation={!!existingHealthcareService}
      >
        {/* Step 1: Service Identity */}
        <FormStep id="service-identity">
          {/* Conformance Alert - keep existing */}
          {!isConformant && (
            <Alert
              icon={<IconAlertTriangle size={16} />}
              title="Data Model Conformance Issues"
              color="yellow"
              mb="md"
            >
              <Text size="sm" mb="xs">
                This service has fields that don't conform to the new data
                model. Please review the highlighted fields below.
              </Text>
              {(conformanceIssues.category || conformanceIssues.type) && (
                <Text size="sm" mt="xs">
                  <Text component="span" fw={500}>
                    Missing Required Fields:
                  </Text>
                  {conformanceIssues.category && (
                    <Text component="span" display="block" mt="xs">
                      • Category: {conformanceIssues.category[0]} (This field
                      will be added in a future update)
                    </Text>
                  )}
                  {conformanceIssues.type && (
                    <Text component="span" display="block" mt="xs">
                      • Type: {conformanceIssues.type[0]} (This field will be
                      added in a future update)
                    </Text>
                  )}
                </Text>
              )}
            </Alert>
          )}

          {/* Batch Edit Mode Indicator */}
          {isEditMode && siblingServices.length > 0 && (
            <Alert
              icon={<IconInfoCircle size={16} />}
              title="Batch Edit Mode"
              color="purple"
              mb="md"
            >
              <Text size="sm">
                Changes to shared fields (Locations, Eligibilities,
                Characteristics, Payment, and Contact Info) will apply to{" "}
                <Text component="span" fw={600}>
                  {siblingServices.length + 1} services
                </Text>{" "}
                by {siblingOrgName || "this organization"}.
              </Text>
            </Alert>
          )}

          <FormField
            label="Provider Organization"
            variant="section"
            required
            error={
              typeof form.errors.providedBy === "string"
                ? form.errors.providedBy
                : typeof form.errors["providerOrganization.name"] === "string"
                  ? form.errors["providerOrganization.name"]
                  : typeof form.errors["providerOrganization.type"] === "string"
                    ? form.errors["providerOrganization.type"]
                    : undefined
            }
          >
            <OrganizationSection
              form={form}
              fieldPath="providerOrganization"
              asyncSearch
              searchQuery={organizationSearchQuery}
              onSearchChange={setOrganizationSearchQuery}
              searchResults={
                Array.isArray(organizationSearchResults)
                  ? (organizationSearchResults as Organization[])
                  : undefined
              }
              isSearching={isSearchingOrganizations}
              selectedOrganization={
                selectedOrganization as Organization | undefined
              }
              disabled={shouldLockProviderOrganization}
              onExistingOrganizationChange={handleOrganizationChange}
              description="Select existing organization or create new one."
              comboboxProps={DROPDOWN_BELOW}
            />

            {/* Duplicate organization warning */}
            {duplicateOrganization &&
              form.values.providerOrganization?.isNew && (
                <Alert
                  icon={<IconAlertTriangle size={16} />}
                  title="Potential duplicate organization"
                  color="yellow"
                  mt="sm"
                >
                  <Text size="sm">
                    An organization named "{duplicateOrganization.name}" with
                    the same type already exists. You can continue, but please
                    confirm this is not a duplicate before creating a new
                    organization.
                  </Text>
                </Alert>
              )}
          </FormField>

          <FormSection title="Healthcare Service" showDivider={false}>
            <FormField
              label="Service Category"
              required
              error={
                typeof form.errors.category === "string"
                  ? form.errors.category
                  : undefined
              }
            >
              <CardSelect
                data={CATEGORY_CARD_OPTIONS}
                value={form.values.category}
                onChange={(value) => {
                  form.setFieldValue("category", value as string);
                  // Clear type when category changes
                  form.setFieldValue("type", "");
                  form.setFieldValue("type-display", "");
                  // Clear types when category changes
                  form.setFieldValue("types", []);
                  form.setFieldValue("types-displays", []);
                }}
                columns={2}
                error={!!form.errors.category}
                disabled={shouldLockServiceCategory}
              />
            </FormField>

            {importMeta?.extractedServices && (
              <ExtractedDataHint
                label="Imported services from website"
                values={importMeta.extractedServices}
                description="We found the following services. Please select the matching types in the field below."
              />
            )}

            <FormField
              label="Service Type"
              required
              error={
                typeof form.errors.types === "string"
                  ? form.errors.types
                  : typeof form.errors.type === "string"
                    ? form.errors.type
                    : undefined
              }
            >
              {existingHealthcareService ? (
                // Edit mode: single select (locked after initial creation)
                <Select
                  placeholder="Select type"
                  data={filteredTypeOptions}
                  searchable
                  clearable
                  disabled={shouldLockServiceType}
                  value={form.values.type}
                  onChange={handleTypeChange}
                  error={form.errors.type}
                  comboboxProps={DROPDOWN_BELOW}
                />
              ) : (
                // Create mode: multi-select
                <MultiSelect
                  placeholder={
                    form.values.category
                      ? "Select one or more types"
                      : "Select a category first"
                  }
                  data={filteredTypeOptions}
                  searchable
                  clearable
                  disabled={!form.values.category}
                  value={form.values.types || []}
                  onChange={handleTypesChange}
                  error={form.errors.types}
                  comboboxProps={DROPDOWN_BELOW}
                />
              )}

              {/* Duplicate warning - updated for multi-select */}
              {duplicateServiceTypes.length > 0 && (
                <Alert
                  icon={<IconAlertTriangle size={16} />}
                  title={
                    existingHealthcareService
                      ? "Potential duplicate service"
                      : "Existing services detected"
                  }
                  color={existingHealthcareService ? "red" : "yellow"}
                  mt="sm"
                >
                  {existingHealthcareService ? (
                    <Text size="sm">
                      A service with the same provider organization and service
                      type already exists. You can continue, but please confirm
                      this is not a duplicate.
                    </Text>
                  ) : (
                    <>
                      <Text size="sm">
                        The following service types already exist for this
                        organization:
                      </Text>
                      <List size="sm" mt="xs">
                        {duplicateServiceTypes.map((type) => {
                          const display =
                            form.values["types-displays"]?.[
                              form.values.types?.indexOf(type) ?? -1
                            ] || type;
                          return <List.Item key={type}>{display}</List.Item>;
                        })}
                      </List>
                      <Text size="sm" mt="xs">
                        These will be skipped. Only new service types will be
                        created.
                      </Text>
                    </>
                  )}
                </Alert>
              )}
            </FormField>

            {/* <FormField label="Service Tier" showDivider={false}>
              <Select
                placeholder="Select partnership type"
                data={SERVICE_CHARACTERISTIC_PARTNERSHIP_OPTIONS}
                clearable
                {...form.getInputProps("partnershipType")}
              />
            </FormField> */}
          </FormSection>
        </FormStep>

        {/* Step 2: Details */}
        <FormStep id="service-details">
          <Alert icon={<IconInfoCircle size={16} />} color="purple" mb="md">
            <Text size="sm">
              Add details on the service's specialties and eligibility criteria.
              (All fields are optional.)
            </Text>
          </Alert>

          <FormSection title="Specialties">
            <FormField
              label="Conditions"
              description="What conditions does this service treat?"
            >
              <MultiSelect
                placeholder="Select conditions"
                data={[...SERVICE_SPECIALTY_TREATMENT_CONDITION_OPTIONS]}
                searchable
                clearable
                value={selectedConditions}
                onChange={handleConditionsChange}
                comboboxProps={DROPDOWN_BELOW}
              />
            </FormField>

            <FormField
              label="Modalities"
              description="What treatment modalities are used?"
            >
              <MultiSelect
                placeholder="Select modalities"
                data={[...SERVICE_SPECIALTY_TREATMENT_MODALITY_OPTIONS]}
                searchable
                clearable
                value={selectedModalities}
                onChange={handleModalitiesChange}
                comboboxProps={DROPDOWN_BELOW}
              />
            </FormField>
          </FormSection>

          <FormField
            label="Eligibilities"
            variant="section"
            description="Who is eligible for this service?"
          >
            <DimensionValueInput
              label="Eligibility"
              showLabel={false}
              dimensionOptions={[...SERVICE_ELIGIBILITY_DIMENSION_OPTIONS]}
              valueOptions={{
                age: [...SERVICE_ELIGIBILITY_AGE_GROUP_OPTIONS],
                gender: [...SERVICE_ELIGIBILITY_GENDER_OPTIONS],
              }}
              values={form.values.eligibility || []}
              onChange={(values) => form.setFieldValue("eligibility", values)}
              comboboxProps={DROPDOWN_BELOW}
            />
          </FormField>
        </FormStep>

        {/* Step 3: Access */}
        <FormStep id="service-access">
          <Alert icon={<IconInfoCircle size={16} />} color="purple" mb="md">
            <Text size="sm">
              Add details on how members can access this service. (All fields
              are optional.)
            </Text>
          </Alert>

          <FormField
            label="Mode"
            variant="section"
            description="How is the service delivered? (Select all that apply.)"
          >
            <MultiSelect
              placeholder="Select visit modes"
              data={[...SERVICE_CHARACTERISTIC_VISIT_MODE_OPTIONS]}
              searchable
              clearable
              comboboxProps={DROPDOWN_BELOW}
              {...form.getInputProps("careModality")}
            />
          </FormField>

          <FormSection title="Access Information">
            <FormGroup>
              <FormField
                label="Availability"
                description="Is this service currently available? (Select all that apply.)"
              >
                <MultiSelect
                  placeholder="Select availability status"
                  data={[...SERVICE_CHARACTERISTIC_AVAILABILITY_OPTIONS]}
                  clearable
                  value={selectedAvailability}
                  onChange={handleAvailabilityChange}
                  comboboxProps={DROPDOWN_BELOW}
                />
              </FormField>

              <FormField
                label="Access"
                description="How can members access this service? (Select one.)"
              >
                <Select
                  placeholder="Select access type"
                  data={[...SERVICE_CHARACTERISTIC_ACCESS_OPTIONS]}
                  clearable
                  value={selectedAccess}
                  onChange={handleAccessChange}
                  comboboxProps={DROPDOWN_BELOW}
                />
              </FormField>
            </FormGroup>
          </FormSection>

          <FormSection title="Scheduling Information">
            <FormGroup>
              <FormField
                label="Scheduling Options"
                description="What scheduling options apply? (Select all that apply.)"
              >
                <MultiSelect
                  placeholder="Select scheduling options"
                  data={[...SERVICE_CHARACTERISTIC_SCHEDULING_OPTIONS]}
                  clearable
                  value={selectedScheduling}
                  onChange={handleSchedulingChange}
                  comboboxProps={DROPDOWN_BELOW}
                />
              </FormField>

              <FormField
                label="Wait Time"
                description="Estimated wait time for new appointments. (Select one.)"
              >
                <Select
                  placeholder="Select wait time"
                  data={[...SERVICE_CHARACTERISTIC_WAIT_TIME_OPTIONS]}
                  clearable
                  value={selectedWaitTime}
                  onChange={handleWaitTimeChange}
                  comboboxProps={DROPDOWN_BELOW}
                />
              </FormField>
            </FormGroup>
          </FormSection>
        </FormStep>

        {/* Step 4: Locations */}
        <FormStep id="service-locations">
          <Alert icon={<IconInfoCircle size={16} />} color="purple" mb="md">
            <Text size="sm">
              Add physical service-delivery locations and the geographic areas
              this service covers. Coverage areas describe the regions a service
              serves (useful for virtual / multi-state services); locations are
              the physical sites where the service is delivered. (All fields are
              optional, but recommended for searchability.)
            </Text>
          </Alert>

          <FormSection title="Coverage Areas" showDivider={false}>
            <FormField
              label="States Served"
              description="Geographic areas this service covers. Useful for virtual or multi-state services."
            >
              <CoverageAreasSection
                form={form}
                fieldPath="coverageAreas"
                comboboxProps={DROPDOWN_BELOW}
              />
            </FormField>
          </FormSection>

          <FormSection title="Service Delivery Locations">
            {importMeta?.extractedLocations && (
              <ExtractedDataHint
                label="Imported addresses from website"
                values={importMeta.extractedLocations}
                description="We found the following addresses. Please add matching locations below."
              />
            )}

            <LocationSection
              form={form}
              fieldPath="locations"
              existingLocations={locationOptions}
              loading={loading.locations}
              comboboxProps={DROPDOWN_BELOW}
            />
          </FormSection>
        </FormStep>

        {/* Step 5: Payment */}
        <FormStep id="service-payment">
          <Alert icon={<IconInfoCircle size={16} />} color="purple" mb="md">
            <Text size="sm">
              Add payment options and insurance plans this service accepts. (All
              fields are optional.)
            </Text>
          </Alert>

          <FormField
            label="Payment Models"
            variant="section"
            description="How is this service paid for?"
          >
            <MultiSelect
              placeholder="Select payment models"
              data={SERVICE_PROVISION_OPTIONS}
              searchable
              clearable
              comboboxProps={DROPDOWN_BELOW}
              {...form.getInputProps("serviceProvisionCode")}
            />
          </FormField>

          {importMeta?.extractedInsurance && (
            <ExtractedDataHint
              label="Imported insurance plans from website"
              values={importMeta.extractedInsurance}
              description="We found the following plans. Please select the matching options below."
            />
          )}

          <FormField
            label="Insurance Acceptance"
            variant="section"
            showDivider={false}
            description="Which insurance plans does this service accept?"
          >
            <MultiSelect
              placeholder={
                loading.insurance ? "Loading..." : "Select insurance plans"
              }
              data={insuranceOrganizationOptions}
              searchable
              clearable
              disabled={loading.insurance}
              comboboxProps={DROPDOWN_BELOW}
              {...form.getInputProps("insurances")}
            />
          </FormField>
        </FormStep>

        {/* Step 6: Misc. */}
        <FormStep id="service-misc">
          <Alert icon={<IconInfoCircle size={16} />} color="purple" mb="md">
            <Text size="sm">
              Add contact information and additional details. (All fields are
              optional.)
            </Text>
          </Alert>

          <FormSection title="Contacts">
            <ContactDetailsSection
              form={form}
              fieldPath="telecom"
              maxContacts={5}
              comboboxProps={DROPDOWN_BELOW}
            />
          </FormSection>

          <FormSection title="Additional Information" showDivider={false}>
            <FormField label="Description">
              <Textarea
                placeholder="Add a brief description of the service"
                minRows={2}
                autosize
                {...form.getInputProps("description")}
              />
            </FormField>

            <FormField label="Extra Details">
              <Textarea
                placeholder="Add additional details that aren't covered in other fields"
                minRows={2}
                autosize
                {...form.getInputProps("extraDetails")}
              />
            </FormField>
          </FormSection>
        </FormStep>
      </SteppedForm>
      <ConfirmationModal />
    </>
  );
};
